import { forwardRef, useEffect, useState } from 'react';
import api from '../../config/axios-conf';
import { getInvoiceSettings } from '../../hooks/useInvoiceSettings';
import { useAuthStore } from '../../stores/authStore';
import { formatCurrency } from '../../utils/formatters';
import styles from '../../assets/scss/components/InvoicePreview.module.scss';

const SNAPSHOT_CUTOVER_TS = new Date('2026-03-21T01:27:00+07:00').getTime();
const LEGACY_REDEEM_VALUE  = 200;

function formatDateTime(str) {
    if (!str) return '';
    const d = new Date(str);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', hour12: false });
}

function formatQuantity(qty) {
    const n = parseFloat(qty) || 0;
    return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(3)));
}

// Group details by serviceId + variantId (same logic as app)
function groupDetails(details = []) {
    const grouped = {};
    details.forEach((item) => {
        const variantId = item.variantId || 'no-variant';
        const key = `${item.billId}_${item.serviceId}_${variantId}`;
        const qty   = parseFloat(item.quantity) || 1;
        const price = parseFloat(item.price)    || 0;
        if (!grouped[key]) {
            grouped[key] = { ...item, quantity: qty, totalPrice: price * qty };
        } else {
            grouped[key].quantity   += qty;
            grouped[key].totalPrice += price * qty;
        }
    });
    return Object.values(grouped);
}

const InvoicePreview = forwardRef(function InvoicePreview({ bill, bankInfo }, ref) {
    const user = useAuthStore((s) => s.user);
    const [shopInfo, setShopInfo]   = useState(null);
    const [paperWidth, setPaperWidth] = useState(80);
    const settings = getInvoiceSettings();

    useEffect(() => {
        if (user?.shopId) {
            api.get(`/shops/${user.shopId}`)
                .then(({ data }) => setShopInfo(data))
                .catch(() => {});
        }
        api.get('/printers')
            .then(({ data }) => {
                const list = data?.data || [];
                const def  = list.find((p) => p.isDefault) || list[0];
                if (def?.paperWidth) setPaperWidth(Number(def.paperWidth));
            })
            .catch(() => {});
    }, [user?.shopId]);

    if (!bill) return null;

    const customer = bill.customer || {};
    const employee = bill.employee || {};
    const details  = bill.details  || [];
    const isKhachLe = customer.name === 'Khách lẻ';

    // Discount calc (same as app)
    const discountAmount  = parseFloat(bill.discount)      || 0;
    const pointUsed       = Number(bill.pointUsed          || 0);
    const checkoutTs      = bill.checkoutTime ? new Date(bill.checkoutTime).getTime() : 0;
    const useSnapshot     = checkoutTs >= SNAPSHOT_CUTOVER_TS && bill.pointCash != null;
    const payByPoint      = useSnapshot ? Number(bill.pointCash || 0) : pointUsed * LEGACY_REDEEM_VALUE;
    const pureDiscount    = Math.max(0, discountAmount - payByPoint);
    const originalTotal   = parseFloat(bill.originalTotal) || 0;
    const total           = parseFloat(bill.total)         || 0;
    const owe             = parseFloat(bill.owe)           || 0;
    const pointReceived   = bill.pointReceived             || 0;

    const grouped = groupDetails(details);

    // Gói đã bán + buổi đã dùng (hoá đơn checkout)
    const soldPackages = bill.customerPackages || [];
    const usageGroups = Object.values(
        (bill.packageUsages || []).reduce((acc, pu) => {
            const key = `${pu.customerPackageId}__${pu.employeeId ?? 0}`;
            if (!acc[key]) {
                acc[key] = {
                    cp: pu.customerPackage,
                    employee: pu.employee,
                    count: 0,
                    recognizedRevenue: Number(pu.recognizedRevenue || 0),
                };
            }
            acc[key].count += 1;
            return acc;
        }, {}),
    );

    // QR
    const qrAmount  = owe > 0 ? Math.round(owe) : Math.round(total);
    const qrAddInfo = encodeURIComponent((bill.code || '') + ' ' + (customer.name || ''));
    const qrUrl     = bankInfo
        ? (bankInfo.qrCodeUrl ||
           `https://img.vietqr.io/image/${bankInfo.bankBin}-${bankInfo.accountNumber}-qr_only.png?amount=${qrAmount}&addInfo=${qrAddInfo}`)
        : null;

    const showShopInfo  = settings.showShopInfo  && !!shopInfo;
    const showQr        = settings.showPaymentQr && !!qrUrl;
    const showTopSection = showShopInfo || showQr;

    // Discount display: show percentage if discountType === '%'
    let discountDisplay = null;
    if (pureDiscount > 0) {
        if (bill.discountType === '%' && originalTotal > 0) {
            const pct = Math.round((pureDiscount / originalTotal) * 1000) / 10;
            discountDisplay = `- ${pct}%`;
        } else {
            discountDisplay = `- ${formatCurrency(pureDiscount)}`;
        }
    }

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <span className={styles.cardTitle}>Xem trước hoá đơn in</span>
                <span className={styles.paperTag}>{paperWidth}mm</span>
            </div>

            <div className={styles.scrollArea}>
                <div ref={ref} className={`${styles.receipt} ${paperWidth === 58 ? styles.receipt58 : styles.receipt80}`}>

                    {/* ===== SHOP INFO + QR ===== */}
                    {showTopSection && (
                        <div className={styles.qrContainer}>
                            {showShopInfo && (
                                <div className={styles.shopInfoContainer}>
                                    <div className={styles.shopName}>{shopInfo.nameShop || shopInfo.name}</div>
                                    {(shopInfo.phoneOne || shopInfo.phoneTwo) && (
                                        <div className={styles.shopPhone}>
                                            {[shopInfo.phoneOne, shopInfo.phoneTwo].filter(Boolean).join(' - ')}
                                        </div>
                                    )}
                                    {shopInfo.address && (
                                        <div className={styles.shopAddress}>{shopInfo.address}</div>
                                    )}
                                </div>
                            )}
                            {showShopInfo && showQr && <div className={styles.verticalDivider} />}
                            {showQr && (
                                <div className={styles.qrWrapper}>
                                    <img src={qrUrl} alt="QR" className={styles.qrImage} />
                                </div>
                            )}
                        </div>
                    )}

                    <hr className={styles.dashed} />

                    {/* ===== TITLE ===== */}
                    <div className={styles.title}>HÓA ĐƠN BÁN HÀNG</div>
                    <div className={styles.subtitle}>{formatDateTime(bill.checkoutTime)}</div>

                    {/* ===== CUSTOMER INFO ===== */}
                    <div className={styles.infoSection}>
                        <div className={styles.customerRow}>
                            <span className={styles.customerLabel}>Khách hàng:</span>
                            <span className={styles.customerName}>{customer.name || 'Khách hàng'}</span>
                        </div>
                        {!isKhachLe && (
                            <>
                                {settings.showCustomerPhone && customer.phone && (
                                    <div className={styles.customerRow}>
                                        <span className={styles.customerLabel}>SĐT:</span>
                                        <span className={styles.customerValue}>{customer.phone}</span>
                                    </div>
                                )}
                                {settings.showCustomerLoyaltyPoint && (
                                    <div className={styles.customerRow}>
                                        <span className={styles.customerLabel}>Tích lũy:</span>
                                        <span className={styles.customerValue}>
                                            {bill.lastPoint ?? customer.point ?? 0} điểm
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                        <div className={styles.customerRow}>
                            <span className={styles.customerLabel}>Nhân viên tạo đơn:</span>
                            <span className={styles.customerValue}>{employee.name || 'N/A'}</span>
                        </div>
                    </div>

                    {/* ===== TABLE HEADER ===== */}
                    <div className={styles.tableHeader}>
                        <span className={`${styles.tableHeaderText} ${styles.thProduct}`}>Sản phẩm</span>
                        <span className={`${styles.tableHeaderText} ${styles.thQty}`}>SL</span>
                        <span className={`${styles.tableHeaderText} ${styles.thPrice}`}>Đơn giá</span>
                        <span className={`${styles.tableHeaderText} ${styles.thAmount}`}>T.Tiền</span>
                    </div>

                    {/* ===== TABLE ROWS ===== */}
                    {grouped.map((item, idx) => {
                        const variantText = Array.isArray(item.variant?.attributeValues)
                            ? item.variant.attributeValues.map((a) => a?.value).filter(Boolean).join(' - ')
                            : '';
                        const originalUnit = Number(item.variant?.retailPrice ?? item.service?.price ?? 0);
                        const actualUnit = Number(item.price) || 0;
                        const priceReduced = originalUnit > 0 && actualUnit < originalUnit;
                        return (
                            <div key={item.id || idx} className={styles.tableRow}>
                                <div className={styles.cellProduct}>
                                    <div className={styles.serviceName}>
                                        {idx + 1}. {item.service?.name}
                                    </div>
                                    {variantText && (
                                        <div className={styles.variantText}>{variantText}</div>
                                    )}
                                </div>
                                <div className={styles.cellQty}>
                                    <span className={styles.tableRowText}>{formatQuantity(item.quantity)}</span>
                                </div>
                                <div className={styles.cellPrice}>
                                    {priceReduced && (
                                        <span
                                            className={styles.tableRowText}
                                            style={{ textDecoration: 'line-through', opacity: 0.55 }}
                                        >
                                            {formatCurrency(originalUnit)}
                                        </span>
                                    )}
                                    <span className={styles.tableRowText}>{formatCurrency(item.price)}</span>
                                </div>
                                <div className={styles.cellAmount}>
                                    {priceReduced && (
                                        <span
                                            className={styles.tableRowText}
                                            style={{ textDecoration: 'line-through', opacity: 0.55 }}
                                        >
                                            {formatCurrency(originalUnit * item.quantity)}
                                        </span>
                                    )}
                                    <span className={styles.tableRowText}>{formatCurrency(item.totalPrice)}</span>
                                </div>
                            </div>
                        );
                    })}

                    {/* ===== GÓI ĐÃ BÁN ===== */}
                    {soldPackages.map((pkg, i) => {
                        const pkgName = pkg.templateSnapshot?.name || `Gói dịch vụ ${pkg.code}`;
                        const sub = [
                            pkg.code ? `Mã thẻ: ${pkg.code}` : '',
                            pkg.totalSessions ? `${pkg.totalSessions} buổi` : '',
                            pkg.sellerEmployee?.name ? `NV bán: ${pkg.sellerEmployee.name}` : '',
                        ]
                            .filter(Boolean)
                            .join(' · ');
                        const contractValue = Number(pkg.totalContractValue) || 0;
                        const snapPrice = pkg.templateSnapshot?.price ?? pkg.package?.price;
                        const priceOverridden =
                            pkg.isOverridden && snapPrice != null && Number(contractValue) !== Number(snapPrice);
                        return (
                            <div key={`pkg-${pkg.id}`} className={styles.tableRow}>
                                <div className={styles.cellProduct}>
                                    <div className={styles.serviceName}>
                                        {grouped.length + i + 1}. {pkgName}
                                    </div>
                                    {sub && <div className={styles.variantText}>{sub}</div>}
                                </div>
                                <div className={styles.cellQty}>
                                    <span className={styles.tableRowText}>1</span>
                                </div>
                                <div className={styles.cellPrice}>
                                    {priceOverridden && (
                                        <span
                                            className={styles.tableRowText}
                                            style={{ textDecoration: 'line-through', opacity: 0.55 }}
                                        >
                                            {formatCurrency(snapPrice)}
                                        </span>
                                    )}
                                    <span className={styles.tableRowText}>{formatCurrency(contractValue)}</span>
                                </div>
                                <div className={styles.cellAmount}>
                                    <span className={styles.tableRowText}>{formatCurrency(contractValue)}</span>
                                </div>
                            </div>
                        );
                    })}

                    {/* ===== BUỔI ĐÃ DÙNG ===== */}
                    {usageGroups.map((g, i) => {
                        const pkgName = g.cp?.templateSnapshot?.name || 'Gói dịch vụ';
                        const sub = [
                            g.cp?.code ? `Mã thẻ: ${g.cp.code} · Dùng buổi` : 'Dùng buổi',
                            g.employee?.name ? `NV: ${g.employee.name}` : '',
                        ]
                            .filter(Boolean)
                            .join(' · ');
                        const amtPerSess = g.recognizedRevenue || Number(g.cp?.amountPerSession || 0);
                        return (
                            <div key={`sess-${i}`} className={styles.tableRow}>
                                <div className={styles.cellProduct}>
                                    <div className={styles.serviceName}>
                                        {grouped.length + soldPackages.length + i + 1}. {pkgName}
                                    </div>
                                    {sub && <div className={styles.variantText}>{sub}</div>}
                                </div>
                                <div className={styles.cellQty}>
                                    <span className={styles.tableRowText}>{g.count}</span>
                                </div>
                                <div className={styles.cellPrice}>
                                    {amtPerSess > 0 && (
                                        <span
                                            className={styles.tableRowText}
                                            style={{ textDecoration: 'line-through', opacity: 0.55 }}
                                        >
                                            {formatCurrency(amtPerSess)}
                                        </span>
                                    )}
                                    <span className={styles.tableRowText}>{formatCurrency(0)}</span>
                                </div>
                                <div className={styles.cellAmount}>
                                    {amtPerSess > 0 && (
                                        <span
                                            className={styles.tableRowText}
                                            style={{ textDecoration: 'line-through', opacity: 0.55 }}
                                        >
                                            {formatCurrency(amtPerSess * g.count)}
                                        </span>
                                    )}
                                    <span className={styles.tableRowText}>{formatCurrency(0)}</span>
                                </div>
                            </div>
                        );
                    })}

                    {/* ===== SUPPORT EMPLOYEES ===== */}
                    {settings.showSupportEmployee && details.length > 0 && (
                        <>
                            <hr className={styles.dashed} style={{ margin: '10px 0' }} />
                            <div className={styles.noteSection}>
                                {details.map((item) => (
                                    <div key={item.id} className={styles.employeeNote}>
                                        NV: {item.employee?.name || 'N/A'} - {item.service?.name}
                                    </div>
                                ))}
                                <div className={styles.warningNote}>
                                    * SL lớn hơn 1 = số nhân viên hỗ trợ trên 1 dịch vụ
                                </div>
                            </div>
                        </>
                    )}

                    {/* ===== SUMMARY ===== */}
                    <div className={styles.summarySection}>
                        <span className={styles.summaryLabel}>Tổng cộng:</span>
                        <span className={styles.summaryValue}>{formatCurrency(originalTotal)}</span>
                    </div>

                    {discountDisplay && (
                        <div className={styles.summarySection}>
                            <span className={styles.summaryLabel}>Giảm giá:</span>
                            <span className={styles.summaryValue}>{discountDisplay}</span>
                        </div>
                    )}

                    {!isKhachLe && settings.showCustomerLoyaltyPoint && pointUsed > 0 && (
                        <div className={styles.summarySection}>
                            <span className={styles.summaryLabel}>Điểm sử dụng:</span>
                            <span className={styles.summaryValue}>- {pointUsed} điểm</span>
                        </div>
                    )}

                    {!isKhachLe && settings.showCustomerLoyaltyPoint && pointReceived > 0 && (
                        <div className={styles.summarySection}>
                            <span className={styles.summaryLabel}>Điểm nhận được:</span>
                            <span className={styles.summaryValue}>+ {pointReceived} điểm</span>
                        </div>
                    )}

                    <div className={styles.summarySection}>
                        <span className={styles.summaryLabel}>Thanh toán:</span>
                        <span className={`${styles.summaryValue} ${styles.paymentMethod}`}>
                            {bill.paymentMethod}
                        </span>
                    </div>

                    {bill.note && (
                        <div className={styles.summarySection}>
                            <span className={styles.summaryLabel}>Ghi chú:</span>
                            <span className={styles.noteText}>{bill.note}</span>
                        </div>
                    )}

                    <hr className={styles.dashed} />

                    {/* ===== TOTAL ===== */}
                    <div className={styles.totalSection}>
                        <span className={styles.totalLabel}>KHÁCH THANH TOÁN :</span>
                        <span className={styles.totalAmount}>{formatCurrency(total)}</span>
                    </div>

                    {owe > 0 && (
                        <div className={styles.summarySection}>
                            <span className={styles.summaryLabel}>Còn nợ:</span>
                            <span className={styles.summaryValue}>{formatCurrency(owe)}</span>
                        </div>
                    )}

                    {/* ===== FOOTER ===== */}
                    <div className={styles.thankYouSection}>
                        <span className={styles.thankYouText}>Cảm ơn quý khách!</span>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default InvoicePreview;
